import { getAccessToken, getRefreshToken, getSessionId, getChatMessageCount, clearAll } from './sessionStorageService';
import logger from '../utils/logger';

/**
 * Service for handling browser closure detection and implicit logout
 * Implements "implicit logout" when user closes browser/tab while logged in
 */

let cleanupExecuted = false;

/**
 * Send session statistics using fetch with keepalive for reliability during page unload
 * @param {string} sessionId - Session ID
 * @param {string} event - Event type ('chat_messages' or 'logout')
 * @param {number} count - Message count
 * @param {string} accessToken - Access token
 */
const sendSessionStatWithKeepalive = (sessionId, event, count, accessToken) => {
  try {
    const payload = { session_id: sessionId, event, count };
    const data = JSON.stringify(payload);
    
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/v1/session-stats`;
    
    // Use fetch with keepalive for better reliability during page unload
    fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: data,
      keepalive: true
    }).then(() => {
      logger.info(`Session stat sent: ${event}`, { count });
    }).catch((error) => {
      logger.warn(`Failed to send session stat ${event}`, { error: error.message });
    });
    
  } catch (error) {
    logger.warn(`Failed to send session stat ${event}`, { error: error.message });
  }
};

/**
 * Perform cleanup actions for implicit logout
 * Called when browser/tab closure is detected
 */
const performImplicitLogout = () => {
  if (cleanupExecuted) return;
  cleanupExecuted = true;

  try {
    logger.info('Browser closure detected - performing implicit logout');

    // 1. Session Statistics: Post final chat_messages (if any) and logout event
    const sessionId = getSessionId();
    const accessToken = getAccessToken();
    
    if (sessionId && accessToken) {
      const count = getChatMessageCount();
      if (count > 0) {
        sendSessionStatWithKeepalive(sessionId, 'chat_messages', count, accessToken);
      }
      
      // Always send logout event
      sendSessionStatWithKeepalive(sessionId, 'logout', 0, accessToken);
    }

    // 2. Clear all session data (synchronous - guaranteed to complete)
    clearAll();
    logger.info('Session storage cleared');

    // 3. Attempt to invalidate refresh token on server (last action, best-effort)
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        // Use sendBeacon for better reliability during page unload
        const logoutData = JSON.stringify({ refresh_token: refreshToken });
        const blob = new Blob([logoutData], { type: 'application/json' });
        
        if (navigator.sendBeacon) {
          // Use sendBeacon if available (more reliable for page unload)
          const success = navigator.sendBeacon('/v1/auth/logout', blob);
          if (success) {
            logger.info('Logout request sent via sendBeacon');
          } else {
            logger.warn('sendBeacon failed, falling back to fetch');
            // Fallback to fetch (may not complete)
            fetch('/v1/auth/logout', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: logoutData,
              keepalive: true
            }).catch(() => {
              // Ignore errors - this is best-effort only
            });
          }
        } else {
          // Fallback to fetch with keepalive
          fetch('/v1/auth/logout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: logoutData,
            keepalive: true
          }).catch(() => {
            // Ignore errors - this is best-effort only
          });
        }
      } catch (error) {
        logger.warn('Failed to send logout request', { error: error.message });
      }
    }

    logger.info('Implicit logout completed');
  } catch (error) {
    logger.error('Error during implicit logout', { error: error.message });
  }
};

/**
 * Setup browser closure detection handlers
 * Should be called once when the app initializes
 */
export const setupCleanupHandlers = () => {
  // Only setup handlers if user is logged in
  const accessToken = getAccessToken();
  if (!accessToken) {
    logger.debug('No active session - skipping cleanup handler setup');
    return;
  }

  logger.info('Setting up browser closure detection handlers');

  // Primary handler: beforeunload event (most reliable)
  window.addEventListener('beforeunload', (event) => {
    logger.debug('beforeunload event triggered');
    performImplicitLogout();
  });

  // Backup handler: pagehide event (modern browsers)
  window.addEventListener('pagehide', (event) => {
    logger.debug('pagehide event triggered');
    performImplicitLogout();
  });

  logger.info('Browser closure detection handlers setup complete');
};

/**
 * Reset cleanup state (useful for testing or if user logs back in)
 */
export const resetCleanupState = () => {
  cleanupExecuted = false;
  logger.debug('Cleanup state reset');
};
